import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import KfLogo from '../../../assets/kf-logo-rosasymbol-svart-cmyk.png'
import FellixSemiBoldFontUrl from '../../../fonts/fellix/Fellix-SemiBold.ttf';
import FellixRegularFontUrl from '../../../fonts/fellix/Fellix-Regular.ttf';
import FellixBoldFontUrl from '../../../fonts/fellix/Fellix-Bold.ttf';
import { næringConfig2, næringConfigV4 } from '../ratingConfigs/ratingConfigs';
import { næringConfigSearchHistory } from '../ratingConfigs/ratingConfigsSearchHistory';
import { getScoreColor } from '../ratingConfigs/ratingColor';

// ************************************************************************
// Farger
// ************************************************************************

// doc.setFillColor(85, 31, 47); // Rød header i KP, #FFFFFF
// doc.setTextColor(246, 234, 190); // Gul tekst i header, #F6EABE
// doc.setFillColor(200, 173, 127); // Gul knapp i KP, #c8ad7f
// doc.setFillColor(4, 79, 103); // Blå knapp i KP, #044F67
// doc.setTextColor(0, 0, 0); // Svart tekst osv, #000000
// doc.setTextColor(255, 255, 255); // Hvit, #FFFFFF

const næringPdfV4 = async (data, user, date, kreditor, orgNumber, localeMessages, downloadSearchHistory = false) => {
  
  // ************************************************************************
  // Hjelpere
  // ************************************************************************

  async function loadFontAsBase64(url) {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const formatDate = (currentDate) => {
    if (!currentDate) return '';
    const day = currentDate.getDate().toString().padStart(2, "0");
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const year = currentDate.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatNumberWithSpaces = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const checkNumberValue = (num) => {
    if (num === '' || num === undefined || num === null || num === 0 || num === '0') {
      return '0';
    }
    return num.toString();
  }

  const checkString = (value) => {
    if (value === undefined || value === null || value === '') {
      return '-'
    }
    return value.toString();
  }

  const setDynamicFontSize = (wordWidth) => {
    if (!wordWidth) return 0;
  
    switch (true) {
      case wordWidth < 70:
        return 20;
      case wordWidth < 80:
        return 16;
      case wordWidth < 90:
        return 14;
      case wordWidth < 100:
        return 12;
      default:
        return 20;
    }
  };

  function getStringDotPath(obj, path) {
    if (!obj || !path) return '';

    const normalized = path.replace(/\[(\d+)\]/g, '.$1');

    return normalized
      .split('.')
      .reduce((current, key) => {
        if (current == null || typeof current !== 'object') return '';
        return current[key];
      }, obj) ?? '';
  }


 function generateAutoTableSingleColumn(
  data,
  config,
  headerPrefix,
  addNæringskode = false,
  downloadSearchHistory = false
) {
  const headerRow = [config.header, headerPrefix];

  const body = config.fields.map(field => {
    const raw = getStringDotPath(data, field.key);
    let cell;

    if (raw === '' || raw == null) {
      cell = '-';
    } else if (downloadSearchHistory && typeof field.format === 'function') {
      cell = field.format(raw);
    } else {
      cell = String(raw);
    }

    return [field.title, cell];
  });

  if (addNæringskode) {
    const title    = næringConfig2[1].fields[0].title;
    const code     = checkString(data.industries?.[0]?.naceCode) ?? '';
    const descr    = checkString(data.industries?.[0]?.description) ?? '';
    const extraVal = `${code} ${descr}`.trim() || '-';
    body.push([title, extraVal]);
  }

  return { head: [headerRow], body };
}


  function generateAutoTableDoubleColumn(dataObject, config1, config2, headerPrefix) {

    // Lager headerRow, sender inn prefix, tom string i dette tilfellet, henter de to andre fra config.
    const headerRow = [headerPrefix, config1.header, config2.header];
  
    // Lager body, viktig at siden begge (config1 og config2) har samme rekkefølge i config siden de deler field titles.
    const body = config1.fields.map((field, index) => {
      const title = field.title;

      // Hent ut alle nivåer via dot-path
      const value1 = getStringDotPath(dataObject, field.key);
      const value2 = getStringDotPath(dataObject, config2.fields[index].key);

      return [title, value1, value2];
    });
  
    return { head: [headerRow], body };
  }

  function generateAutoTableRegnskap(dataObject, config, downloadSearchHistory = false) {
  let items = dataObject.financialStatements;
  if (!items) {
    items = [];
  } else if (!Array.isArray(items)) {
    items = [items];
  }

  const subheaderLabels = items.map(item => item[config.subheaderKey]);
  const headerRow1 = [config.groupHeaders[0] || ''].concat(subheaderLabels);
  const headerRow2 = [config.groupHeaders[1] || ''].concat(subheaderLabels);
  const headerRow3 = [config.groupHeaders[2] || ''].concat(subheaderLabels);

  const [size1, size2] = config.groupSizes;
  const group1Fields = config.fields.slice(0, size1);
  const group2Fields = config.fields.slice(size1, size1 + size2);
  const group3Fields = config.fields.slice(size1 + size2);

  const buildBodyRows = fieldsGroup =>
    fieldsGroup.map(field => {
      const row = [field.title];

      items.forEach(item => {
        const raw = item[field.key];
        let cell;

        if (raw == null) {
          cell = '';
        } else if (downloadSearchHistory && typeof field.format === 'function') {
          cell = field.format(raw);
        } else {
          cell = checkString(raw);
        }

        row.push(cell);
      });

      return row;
    });

  const body1 = buildBodyRows(group1Fields);
  const body2 = buildBodyRows(group2Fields);
  const body3 = buildBodyRows(group3Fields);

  return [
    { head: [headerRow1], body: body1 },
    { head: [headerRow2], body: body2 },
    { head: [headerRow3], body: body3 },
  ];
}


// Brukes for aksjer og aksjonærer
function generateAutoTableDataForMultiBlock(dataArray, config, headerPrefix, spacerRowCount = 1) {
    // Lager blokker med data for hver aksjonær/aksje
    const blocks = (dataArray || []).map(dataObj => {
        const result = generateAutoTableSingleColumn(dataObj, config, headerPrefix);
        return result.body; // Hver rad er [title, key]
    });
    
    const blockRowCount = config.fields.length;
    const combinedRows = [];
    
    // To blokker på hver rad
    for (let i = 0; i < blocks.length; i += 2) {
      const leftBlock = blocks[i];
      const rightBlock = (i + 1 < blocks.length)
        ? blocks[i + 1]
        : Array.from({ length: blockRowCount }, () => ['', '']);
      
      for (let rowIndex = 0; rowIndex < blockRowCount; rowIndex++) {
        const combinedRow = [...leftBlock[rowIndex], ...rightBlock[rowIndex]];
        combinedRows.push(combinedRow);
      }
      
      const numCells = 4;
      const emptyRow = new Array(numCells).fill('');
      for (let s = 0; s < spacerRowCount; s++) {
        combinedRows.push(emptyRow);
        combinedRows.push(emptyRow);
      }
    }

    // Placeholder om ingen data
    if (combinedRows.length === 0) {
        combinedRows.push(['Ingen resultat', '', '', '']);
    }
    
    return { head: [], body: combinedRows };
  }

function generateAutoTableDataFromListValueSingle(
  data,
  config,
  downloadSearchHistory = false
) {
  let items = data;
  if (!items) {
    items = [];
  } else if (!Array.isArray(items)) {
    items = [items];
  }

  const headerRow = config.fields.map(field => field.title);

  const body = items.map(item =>
    config.fields.map(field => {
      const raw = getStringDotPath(item, field.key);

      if (downloadSearchHistory && typeof field.format === 'function') {
        return field.format(raw);
      }

      return raw;
    })
  );

  if (body.length === 0) {
    const placeholderRow = new Array(config.fields.length).fill('');
    placeholderRow[0] = 'Ingen data å vise';
    return { head: [headerRow], body: [placeholderRow] };
  }

  return { head: [headerRow], body };
}
    

  // ************************************************************************
  // Lag dokument
  // ************************************************************************
  
  // Lag nytt jsPDF dokument
  const doc = new jsPDF();

  // Funksjon for å sjekke om ny side må legges til
  const addNewPageIfNeeded = (doc, currentY, marginBottom = 10) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (currentY > pageHeight - marginBottom) {
      doc.addPage();
      return 20; // ny y-koordinat med litt margin
    }
    return currentY;
  };

  const getFileName = () => {
    const nameValue = data.organisationInformation.organisationName && data.organisationInformation.organisationName.trim() !== '' 
        ? data.organisationInformation.organisationName 
        : '';
    
    return `Kredittvurdering ${nameValue}.pdf`;
  };


  // ************************************************************************
  // Fonter
  // ************************************************************************

  // Laster og registrerer alle fonter
  const boldBase64 = await loadFontAsBase64(FellixBoldFontUrl);
  doc.addFileToVFS('Fellix-Bold.ttf', boldBase64);
  doc.addFont('Fellix-Bold.ttf', 'Fellix', 'bold');

  const semiboldBase64 = await loadFontAsBase64(FellixSemiBoldFontUrl);
  doc.addFileToVFS('Fellix-SemiBold.ttf', semiboldBase64);
  doc.addFont('Fellix-SemiBold.ttf', 'Fellix', 'semiBold');

  const regularBase64 = await loadFontAsBase64(FellixRegularFontUrl);
  doc.addFileToVFS('Fellix-Regular.ttf', regularBase64);
  doc.addFont('Fellix-Regular.ttf', 'Fellix', 'normal');


  // ************************************************************************
  // Sidenummer
  // ************************************************************************

  function findPageNumber(doc, pageData) {
    
    doc.setFont("Fellix", "semiBold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
  
    const currentPage = doc.internal.getNumberOfPages();
    const totalPagesExp = '{total_pages_count_string}'; // Denne blir satt helt på slutten
    let pageStr = "Side " + currentPage;
    if (typeof doc.putTotalPages === 'function') {
        pageStr += " av " + totalPagesExp;
    }
    const pageSize = doc.internal.pageSize;
    const pageHeight = pageSize.height || pageSize.getHeight();
    // Sidenummer skal være nederst til venstre
    doc.text(pageStr, pageData.settings.margin.left, pageHeight - 10);
  }


  // ************************************************************************
  // Header
  // ************************************************************************

  // Bestemmer header høyde og bredden på pdf. (units, e.g., mm)
  const headerHeight = 30;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Farger og lager header som Logo og bruker-tekst skal inn i
  doc.setFillColor(255, 255, 255); // Rød header i KP, #FFFFFF
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(10);

  // Header-teksten skal være på venstre side
  let currentY = 0; // Hjelper for å kunne holde styr på hvor vi er og om vi må legge til ny side
  const leftMargin = 10; // Margin venstre som tekst bruker
  const kredittvurderingY = 16; // y-koordinator for kredittvurdering linjen
  const dateY = 21; // y-koordinater for dato linjen
  const kreditorY = 26; // y-koordinater for kreditor linjen
  const userY = 31; // y-koordinater for bruker linjen
  const marginBottom = 10; // Brukes når vi sjekker om vi må legge til ny side
  const verticalSpacing = 12; // Vertikal spacing mellom tittel og verdi
  const pad = 5; // Padding for hver kolonne
  const marginLeft = 10;
  const marginRight = 10;
  const totalTableWidth = pageWidth - marginLeft - marginRight;


  // Sub header verdier
  const headerLeftMargin = 8; // Margin for sub headers
  const headerRightMargin = 8; // Margin for sub headers
  const subHeaderHeight = 12; // Høyde for sub headers
  const subHeaderWidth = pageWidth - headerLeftMargin - headerRightMargin; // Bredde for sub headers
  const cornerRadius = 3; // Avrundede hjørner for sub headers
  const subHeaderTekstX = headerLeftMargin + 3; // Teksten i sub headers skal ha en liten margin til venstre
  let subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Plasserer teksten vertikalt i sub headers, denne oppdateres underveis

  // Type kredittvurdering
  doc.setFont("Fellix", "normal");
  doc.text("Type: ", leftMargin, kredittvurderingY);
  const typeLabelWidth = doc.getTextWidth("Type: ");
  doc.setFont("Fellix", "semiBold");
  doc.text("Kredittvurdering", leftMargin + typeLabelWidth, kredittvurderingY);

  // Kreditor som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Kreditor: ", leftMargin, kreditorY);
  const kreditorLabelWidth = doc.getTextWidth("Kreditor: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(kreditor), leftMargin + kreditorLabelWidth, kreditorY);

  // Dato utført
  doc.setFont("Fellix", "normal");
  doc.text("Dato utført: ", leftMargin, dateY);
  const dateLabelWidth = doc.getTextWidth("Dato utført: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(formatDate(date), leftMargin + dateLabelWidth, dateY);

  // Bruker som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Utført av: ", leftMargin, userY);
  const userLabelWidth = doc.getTextWidth("Utført av: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(user), leftMargin + userLabelWidth, userY);

  // Logo
  const logoWidth = 40;  // Logo sin bredde i mm
  const logoHeight = 10; // Logo sin høyde i mm
  const logoX = pageWidth - logoWidth - 10; // Margin fra høyre kant
  const logoY = 16; // Sentrer i midten av header
  doc.addImage(KfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);


  // ************************************************************************
  // Foretaksnavn og score
  // ************************************************************************

  // Foretaksnavn
  currentY += 55; // y-koordinater for navn linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setFont("Fellix", "bold");
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(22);
  doc.text(checkString(data.organisationInformation.organisationName), leftMargin, currentY);

  currentY += 10; // y-koordinator for personnr og type tag
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Orgnr og type tag skal være rett under navn
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(14);
  doc.text(orgNumber, leftMargin, currentY);

  const orgNumberWidth = doc.getTextWidth(orgNumber);
  // Kalkulerer bredden til tekst for sentrering i rektangel
  const typeWidth = doc.getTextWidth("Næring");
  const typeRectangleWidth = typeWidth;
  const typeRectangleHeight = 6;
  const maxTextWidth = 50;

  // Rektangel sin x-posisjon skal være rett etter personnr + litt margin
  const rectangleX = leftMargin + orgNumberWidth + 4;

  // Setter svart border og line width for rektangel
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);

  // Lager rektangel for type med 1mm border radius
  doc.roundedRect(rectangleX, currentY - 5, typeRectangleWidth, typeRectangleHeight, 1, 1, 'S'); 
  
  const typeX = rectangleX + (typeRectangleWidth - typeWidth) / 2;

  // Add the text inside the rectangle
  doc.setFontSize(10);
  doc.text("Næring", typeX + 2.5, currentY - 0.7);

  currentY += 15; // y-koordinater for generell info linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  const scoreSpacing = 10;

   // Kalkulerer bredde for alle kolonnene
  // Kolonne 1: Rating
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col1LabelWidth = doc.getTextWidth(localeMessages['gradeLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col1ValueWidth = doc.getTextWidth(String(data.risk.scoreRating)) + pad;
  const col1Width = Math.max(col1LabelWidth, col1ValueWidth);

  // Kolonne 2: Score
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col2LabelWidth = doc.getTextWidth(localeMessages['scoreLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col2ValueWidth = doc.getTextWidth(String(data.risk.normalisedScore)) + pad;
  const col2Width = Math.max(col2LabelWidth, col2ValueWidth);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col3LabelWidth = doc.getTextWidth(localeMessages['ratingLabel']) + pad;
  doc.setFont("Fellix", "normal");
  doc.setFontSize(20);
  const col3ValueWidth = doc.getTextWidth(data.risk.scoreRatingExplanation) + pad;
  const col3Width = Math.max(col3LabelWidth, col3ValueWidth);
  const reduserVurderingWidth = col3ValueWidth > 60;

  // Kalkulerer x-posisjoner basert på bredde
  const scoreCol1X = leftMargin;
  const scoreCol2X = scoreCol1X + col1Width + scoreSpacing;
  const scoreCol3X = scoreCol2X + col2Width + scoreSpacing;
  const scoreCol4X = reduserVurderingWidth ? (scoreCol3X - 13) + col3Width + scoreSpacing : scoreCol3X + col3Width + scoreSpacing;

  // Kolonne 1: Rating
  doc.setTextColor(0, 0, 0);
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['gradeLabel'], scoreCol1X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.setTextColor(getScoreColor(data.risk.scoreRating, 'company'));
  doc.text(checkString(data.risk.scoreRating), scoreCol1X, currentY + verticalSpacing);

  // Kolonne 2: Score
  doc.setTextColor(0, 0, 0);
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['scoreLabel'], scoreCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkNumberValue(data.risk.normalisedScore), scoreCol2X, currentY + verticalSpacing);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['ratingLabel'], scoreCol3X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(setDynamicFontSize(col3ValueWidth)); // Justerer fontsize basert på vurdering sin lengde
  doc.text(checkString(data.risk.scoreRatingExplanation), scoreCol3X, currentY + verticalSpacing);

  // Kolonne 4: Limit
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['recommended_limitLabel'], scoreCol4X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.risk.creditLimit), scoreCol4X, currentY + verticalSpacing);  



  // ************************************************************************
  // Grunndata
  // ************************************************************************
  
  // Generell informasjon får sin egen side siden det er mye informasjon
  currentY += 30;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Foretaksinformasjon", subHeaderTekstX, subHeaderTekstY);

  currentY += 20;

  const foretaksConfig = næringConfig2[0];

  const foretaksData = generateAutoTableSingleColumn(data.organisationInformation, downloadSearchHistory ? næringConfigSearchHistory.grunndata : foretaksConfig, '', true, downloadSearchHistory);

  autoTable(doc, {
    head: foretaksData.head,
    body: foretaksData.body,
    theme: 'striped',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 140, fontStyle: 'bold' },
    },
    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });


  // ************************************************************************
  // Adresse
  // ************************************************************************
  
  currentY += 85;
    
  const forretningsAdrConfig = næringConfig2[2];
  const postAdrConfig = næringConfig2[3];

  const adresseTableData = generateAutoTableDoubleColumn(data.organisationInformation, forretningsAdrConfig, postAdrConfig, 'Adresser');


  autoTable(doc, {
    head: adresseTableData.head,
    body: adresseTableData.body,
    theme: 'striped',
    styles: {
        font: "Fellix",
        fontSize: 10,
    },
    headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 70, fontStyle: 'bold' },
        2: { cellWidth: 70, fontStyle: 'bold' },
    },
    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
    didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
      }
  });



  // ************************************************************************
  // Oversikt -> ny side
  // ************************************************************************
 
  // Høyde for Oversikt header
  doc.addPage(); 
  currentY = 20;

  // Farger og lager sub header
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Oversikt", subHeaderTekstX, subHeaderTekstY); 

  currentY += 20; // Litt whitespace mellom subheader og table under

  doc.setTextColor(0, 0, 0);

  // Status
  const oversiktConfig = næringConfig2[4];
  const oversiktData = generateAutoTableSingleColumn(data.risk, downloadSearchHistory ? næringConfigSearchHistory.oversikt :  oversiktConfig, '', false, downloadSearchHistory);

  autoTable(doc, {
    head: oversiktData.head,
    body: oversiktData.body,
    theme: 'striped',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fillColor: [120, 120, 120],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 60, fontStyle: 'bold' },
    },

    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });

  // Pant skal være til høyre for Oversikt, finner x-posisjon
  const gap = 20;
  const secondColumnX = marginLeft + 100 + gap;

  // Pant
  const pantConfig = næringConfig2[5];
  const pantData = generateAutoTableSingleColumn(data.risk, downloadSearchHistory ? næringConfigSearchHistory.pant : pantConfig, '', false,  downloadSearchHistory);

  autoTable(doc, {
    head: pantData.head,
    body: pantData.body,
    theme: 'striped',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fillColor: [120, 120, 120],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 30, fontStyle: 'bold' },
    },
    // startX: 90,
    tableWidth: totalTableWidth,
    margin: { left: secondColumnX, right: marginRight },
    startY: currentY,
  });

  currentY += 95;

  // Eiendeler
  const eiendelerConfig = næringConfig2[6];
  const eiendelerData = generateAutoTableSingleColumn(data, eiendelerConfig, '');

  autoTable(doc, {
    head: eiendelerData.head,
    body: eiendelerData.body,
    theme: 'striped',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fillColor: [120, 120, 120],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 60, fontStyle: 'bold' },
    },
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });

  
  // ************************************************************************
  // Roller
  // ************************************************************************
 
  // Høyde for Roller sub header
  currentY += 35; // Ny side om >= 300
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Farger og lager header
  doc.setFillColor(255, 255, 255); // Gul knapp i KP, #c8ad7f
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Roller", subHeaderTekstX, subHeaderTekstY);

  currentY += 20; // Vertikal posisjon for Roller-verdiene

  const rollerData = generateAutoTableDataFromListValueSingle(data.roles, downloadSearchHistory ? næringConfigSearchHistory.roles : næringConfigV4.roles,
    downloadSearchHistory);

  autoTable(doc, {
    head: rollerData.head,
    body: rollerData.body,
    theme: 'striped',
    styles: {
        font: "Fellix",
        fontStyle: 'bold',
        fontSize: 10,
    },
    headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
    },
    startY: currentY,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
      }
  });

  // ************************************************************************
  // Regnskap
  // ************************************************************************

  // Regnskap får sin egen side siden det er mye informasjon
  doc.addPage(); 
  currentY = 20;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Regnskap", subHeaderTekstX, subHeaderTekstY);

  currentY += 20;
  
  // Genererer dataen basert på kredittvurderingsdataen og config
  const regnskapGroups = generateAutoTableRegnskap(data, downloadSearchHistory ? næringConfigSearchHistory.financialStatements : næringConfigV4.financialStatements, downloadSearchHistory);

  regnskapGroups.forEach(groupData => {
    autoTable(doc, {
        head: groupData.head,
        body: groupData.body,
        theme: 'striped',
        styles: {
            font: "Fellix",
            fontSize: 10,
        },
        headStyles: {
          fillColor: [120, 120, 120],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 12,
        },
        // Setter bredde slik at alle kolonner er på linje vertikalt
        columnStyles: {
            0: { cellWidth: 90 },
            1: { cellWidth: 35, fontStyle: 'bold' },
            2: { cellWidth: 35, fontStyle: 'bold' },
            3: { fontStyle: 'bold' },
        },
        startY: currentY,
        startX: marginLeft,
        tableWidth: totalTableWidth,
        margin: { left: marginLeft, right: marginRight },
        didDrawPage: function (pageData) {
            findPageNumber(doc, pageData);
          }
    });
    currentY = doc.lastAutoTable.finalY + 5;
  });


  // ************************************************************************
  // Betalingsanmerkninger
  // ************************************************************************
 
  const betalingsanmerkningerData = generateAutoTableDataFromListValueSingle(
    downloadSearchHistory
      ? data.risk.legalRecords.paymentRemarks
      : data.legalRecords.paymentRemarks,
    downloadSearchHistory ? næringConfigSearchHistory.paymentRemarks : næringConfigV4.paymentRemarks,
    downloadSearchHistory
  );

  const isPaymentRemarksEmpty =
    betalingsanmerkningerData.body.length === 1 &&
    betalingsanmerkningerData.body[0][0] === 'Ingen data å vise';

  if (!isPaymentRemarksEmpty) {
    doc.addPage();
    currentY = 20;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius, cornerRadius,
      'F'
    );

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subY3 = currentY + subHeaderHeight / 2 + 2;
    doc.text('Betalingsanmerkninger', subHeaderTekstX, subY3);

    currentY += 20;

    autoTable(doc, {
      head: betalingsanmerkningerData.head,
      body: betalingsanmerkningerData.body,
      theme: 'striped',
      styles: { 
        font: 'Fellix', 
        fontStyle: 'bold', 
        fontSize: 8 
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      startY:     currentY,
      tableWidth: totalTableWidth,
      margin:     { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }


  // ************************************************************************
  // Pant
  // ************************************************************************
 
  const movableAssetsData = generateAutoTableDataFromListValueSingle(
    downloadSearchHistory
      ? data.risk.legalRecords.movableAssets
      : data.legalRecords.movableAssets,
    downloadSearchHistory ? næringConfigSearchHistory.movableAssets : næringConfigV4.movableAssets,
    downloadSearchHistory
  );

  const isMovableAssetsEmpty =
    movableAssetsData.body.length === 1 &&
    movableAssetsData.body[0][0] === 'Ingen data å vise';

  if (!isMovableAssetsEmpty) {
    doc.addPage();
    currentY = 20;

    // Subheader background
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius, cornerRadius,
      'F'
    );

    // Subheader text
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subY4 = currentY + subHeaderHeight / 2 + 2;
    doc.text('Pant', subHeaderTekstX, subY4);

    currentY += 20;

    autoTable(doc, {
      head: movableAssetsData.head,
      body: movableAssetsData.body,
      theme: 'striped',
      styles: { 
        font: 'Fellix', 
        fontStyle: 'bold', 
        fontSize: 10 
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      startY:     currentY,
      tableWidth: totalTableWidth,
      margin:     { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }


  // ************************************************************************
  // Tvangssalg
  // ************************************************************************
 
  const propertyForeclosuresData = generateAutoTableDataFromListValueSingle(
    downloadSearchHistory
      ? data.risk.legalRecords.propertyForeclosures
      : data.legalRecords.propertyForeclosures,
    downloadSearchHistory ? næringConfigSearchHistory.propertyForeclosures : næringConfigV4.propertyForeclosures,
    downloadSearchHistory
  );

  const isPropertyForeclosuresEmpty =
    propertyForeclosuresData.body.length === 1 &&
    propertyForeclosuresData.body[0][0] === 'Ingen data å vise';

  if (!isPropertyForeclosuresEmpty) {
    doc.addPage();
    currentY = 20;

    doc.setFillColor(255,255,255);
    doc.roundedRect(
      headerLeftMargin, currentY,
      subHeaderWidth, subHeaderHeight,
      cornerRadius, cornerRadius, 'F'
    );
    doc.setTextColor(0,0,0);
    doc.setFontSize(16);
    doc.setFont('Fellix','bold');
    const subY1 = currentY + subHeaderHeight/2 + 2;
    doc.text('Tvangsfullbyrdelse av eiendom', subHeaderTekstX, subY1);

    currentY += 20;

    autoTable(doc, {
      head: propertyForeclosuresData.head,
      body: propertyForeclosuresData.body,
      theme: 'striped',
      styles: { 
        font:'Fellix', 
        fontStyle:'bold', 
        fontSize:10 
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      startY:     currentY,
      tableWidth: totalTableWidth,
      margin:     { left:marginLeft, right:marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }


  // ************************************************************************
  // Merknader om eiendomstvangsforretning
  // ************************************************************************
 
  const propertyEnforcedActionRemarksData = generateAutoTableDataFromListValueSingle(
    downloadSearchHistory
      ? data.risk.legalRecords.propertyEnforcedActionRemarks
      : data.legalRecords.propertyEnforcedActionRemarks,
    downloadSearchHistory ? næringConfigSearchHistory.propertyEnforcedActionRemarks : næringConfigV4.propertyEnforcedActionRemarks,
    downloadSearchHistory
  );

  const isRemarksEmpty =
    propertyEnforcedActionRemarksData.body.length === 1 &&
    propertyEnforcedActionRemarksData.body[0][0] === 'Ingen data å vise';

  if (!isRemarksEmpty) {
    doc.addPage();
    currentY = 20;

    doc.setFillColor(255,255,255);
    doc.roundedRect(
      headerLeftMargin, currentY,
      subHeaderWidth, subHeaderHeight,
      cornerRadius, cornerRadius, 'F'
    );
    doc.setTextColor(0,0,0);
    doc.setFontSize(16);
    doc.setFont('Fellix','bold');
    const subY2 = currentY + subHeaderHeight/2 + 2;
    doc.text('Merknader om eiendomstvangsforretning', subHeaderTekstX, subY2);

    currentY += 20;

    autoTable(doc, {
      head: propertyEnforcedActionRemarksData.head,
      body: propertyEnforcedActionRemarksData.body,
      theme: 'striped',
      styles: { 
        font:'Fellix', 
        fontStyle:'bold', 
        fontSize:8 
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      startY:     currentY,
      tableWidth: totalTableWidth,
      margin:     { left:marginLeft, right:marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }

  // ************************************************************************
  // Aksjer
  // ************************************************************************
 
  const aksjerData = generateAutoTableDataFromListValueSingle(
    data.shares,
    downloadSearchHistory ? næringConfigSearchHistory.shares : næringConfigV4.shares,
    downloadSearchHistory
  );
  const isAksjerEmpty =
    aksjerData.body.length === 1 &&
    aksjerData.body[0][0] === 'Ingen data å vise';

  if (!isAksjerEmpty) {
    doc.addPage();
    currentY = 20;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius,
      cornerRadius,
      'F'
    );

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subHeaderY = currentY + subHeaderHeight / 2 + 2;
    doc.text('Aksjer', subHeaderTekstX, subHeaderY);

    currentY += 20;

    autoTable(doc, {
      head: aksjerData.head,
      body: aksjerData.body,
      theme: 'striped',
      styles: {
        font: 'Fellix',
        fontStyle: 'bold',
        fontSize: 10,
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }


  // ************************************************************************
  // Aksjonærer
  // ************************************************************************
 
  const aksjonaererData = generateAutoTableDataFromListValueSingle(
    data.shareholders,
    downloadSearchHistory ? næringConfigSearchHistory.shareholders : næringConfigV4.shareholders,
    downloadSearchHistory
  );
  const isAksjonaererEmpty =
    aksjonaererData.body.length === 1 &&
    aksjonaererData.body[0][0] === 'Ingen data å vise';

  if (!isAksjonaererEmpty) {
    doc.addPage();
    currentY = 20;

    // Subheader background
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius,
      cornerRadius,
      'F'
    );

    // Subheader text
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subHeaderY2 = currentY + subHeaderHeight / 2 + 2;
    doc.text('Aksjonærer', subHeaderTekstX, subHeaderY2);

    currentY += 20;

    autoTable(doc, {
      head: aksjonaererData.head,
      body: aksjonaererData.body,
      theme: 'striped',
      styles: {
        font: 'Fellix',
        fontStyle: 'bold',
        fontSize: 10,
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }

  // ************************************************************************
  // Eiendom
  // ************************************************************************
 
  // Lager ny side siden Eiendom kan være veldig lang
  const propertyData = generateAutoTableDataFromListValueSingle(
    data.properties,
    downloadSearchHistory ? næringConfigSearchHistory.properties : næringConfigV4.properties,
    downloadSearchHistory
  );

  // 2) Detect the “no data” placeholder
  const isPropertyEmpty =
    propertyData.body.length === 1 &&
    propertyData.body[0][0] === 'Ingen data å vise';

  if (!isPropertyEmpty) {
    doc.addPage();
    currentY = 20;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius,
      cornerRadius,
      'F'
    );

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subHeaderTekstY = currentY + subHeaderHeight / 2 + 2;
    doc.text('Eiendom', subHeaderTekstX, subHeaderTekstY);

    currentY += 20;

    autoTable(doc, {
      head: propertyData.head,
      body: propertyData.body,
      theme: 'striped',
      styles: {
        font: 'Fellix',
        fontStyle: 'bold',
        fontSize: 6,
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData),
    });
  }

  // ************************************************************************
  // Heftelser (Eiendom)
  // ************************************************************************
   
  function flattenPropertyLiens(data) {
    return (data.properties || []).flatMap(property => {
      const share = property.info.shareOfOwnership;
      const propType = property.info.propertyType;
      const liens = Array.isArray(property.liens)
        ? property.liens
        : property.liens
          ? [property.liens]
          : [];

      return liens.map(lien => ({
        ...lien,
        shareOfOwnership: share,
        propertyType:     propType,
      }));
    });
  }

  const propertyLiensData = generateAutoTableDataFromListValueSingle(
    flattenPropertyLiens(data),
    downloadSearchHistory ? næringConfigSearchHistory.propertiesLiens : næringConfigV4.propertiesLiens,
    downloadSearchHistory
  );

  const isPropertyLiensEmpty =
    propertyLiensData.body.length === 1 &&
    propertyLiensData.body[0][0] === 'Ingen data å vise';

  if (!isPropertyLiensEmpty) {
    doc.addPage();
    currentY = 20;

    // Sub‑header bakgrunn
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius,
      cornerRadius,
      'F'
    );

    // Sub‑header tekst
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subHeaderTekstY = currentY + subHeaderHeight / 2 + 2;
    doc.text('Heftelser (Eiendom)', subHeaderTekstX, subHeaderTekstY);

    currentY += 20;

    // Tegn selve tabellen
    autoTable(doc, {
      head: propertyLiensData.head,
      body: propertyLiensData.body,
      theme: 'striped',
      styles: {
        font: 'Fellix',
        fontStyle: 'bold',
        fontSize: 6
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData)
    });
  }
  

  // ************************************************************************
  // Kjøretøy
  // ************************************************************************
 
  // Lager ny side siden Kjøretøy kan være veldig lang
  const vehicleData = generateAutoTableDataFromListValueSingle(
    data.vehicles,
    downloadSearchHistory ? næringConfigSearchHistory.vehicles : næringConfigV4.vehicles,
    downloadSearchHistory
  );

  const isVehicleEmpty =
    vehicleData.body.length === 1 &&
    vehicleData.body[0][0] === 'Ingen data å vise';

  if (!isVehicleEmpty) {
    doc.addPage();
    let currentY = 20;

    // sub‑header
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont("Fellix", "bold");
    const subHeaderTekstY = currentY + subHeaderHeight / 2 + 2;
    doc.text("Kjøretøy", subHeaderTekstX, subHeaderTekstY);

    currentY += 20;

    autoTable(doc, {
      head: vehicleData.head,
      body: vehicleData.body,
      theme: 'striped',
      styles: {
        font: "Fellix",
        fontStyle: 'bold',
        fontSize: 10,
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: (pageData) => findPageNumber(doc, pageData),
    });
  }


  // ************************************************************************
  // Heftelser (Kjøretøy)
  // ************************************************************************
  
  function flattenVehicleLiens(data) {
    return (data.vehicles || []).flatMap(vehicle => {
      const propType = vehicle.info.vehicleType;
      const liens = Array.isArray(vehicle.liens)
        ? vehicle.liens
        : vehicle.liens
          ? [vehicle.liens]
          : [];

      return liens.map(lien => ({
        ...lien,
        vehicleType: propType,
      }));
    });
  }
  const vehicleLiensData = generateAutoTableDataFromListValueSingle(
    flattenVehicleLiens(data),
    downloadSearchHistory ? næringConfigSearchHistory.vehicleLiens : næringConfigV4.vehicleLiens,
    downloadSearchHistory
  );

  const isVehicleLiensEmpty =
    vehicleLiensData.body.length === 1 &&
    vehicleLiensData.body[0][0] === 'Ingen data å vise';

  if (!isVehicleLiensEmpty) {
    doc.addPage();
    currentY = 20;

    // Sub‑header bakgrunn
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(
      headerLeftMargin,
      currentY,
      subHeaderWidth,
      subHeaderHeight,
      cornerRadius,
      cornerRadius,
      'F'
    );

    // Sub‑header tekst
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('Fellix', 'bold');
    const subHeaderTekstY = currentY + subHeaderHeight / 2 + 2;
    doc.text('Heftelser (Kjøretøy)', subHeaderTekstX, subHeaderTekstY);

    currentY += 20;

    // Tegn selve tabellen
    autoTable(doc, {
      head: vehicleLiensData.head,
      body: vehicleLiensData.body,
      theme: 'striped',
      styles: {
        font: 'Fellix',
        fontStyle: 'bold',
        fontSize: 6
      },
      headStyles: {
        fillColor: [120, 120, 120],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
      },
      startY: currentY,
      tableWidth: totalTableWidth,
      margin: { left: marginLeft, right: marginRight },
      didDrawPage: pageData => findPageNumber(doc, pageData)
    });
  }

  
  // Setter total antall sider helt til slutt
  if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages('{total_pages_count_string}');
    }


  return doc.output('blob')

};

export default næringPdfV4;
